import React, { ReactNode, memo, useEffect, useRef } from "react";
import { MapMarkersGroup } from "../MapView";
import L, { LeafletEvent } from "leaflet";
import * as ReactDOM from "react-dom/client";
import "leaflet.markercluster";

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
    iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
    shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

export interface MapMarker {
    coordinates: [number, number];
    popupContent?: ReactNode;
}

interface MapMarkersPaneProps extends MapMarkersGroup {
    map?: L.Map | null;
}

// Component to render popup content
const PopupContent = memo(({ contents }: { contents: ReactNode[] }) => (
    <div className="marker-popup-content">
        {contents.map(
            (content, idx) =>
                content && (
                    <React.Fragment key={idx}>
                        <div className="marker-content">{content}</div>
                        {idx < contents.filter(Boolean).length - 1 && <hr />}
                    </React.Fragment>
                )
        )}
    </div>
));

const MapMarkersPane: React.FC<MapMarkersPaneProps> = memo(({ markers, clustered, map }) => {
    const markerClusterRef = useRef<L.MarkerClusterGroup | null>(null);
    const markersRef = useRef<L.Marker[]>([]);
    const popupContainerRef = useRef<HTMLDivElement | null>(null);
    const popupRootRef = useRef<ReactDOM.Root | null>(null);

    // Initialize popup container and root
    useEffect(() => {
        if (!popupContainerRef.current) {
            popupContainerRef.current = document.createElement("div");
            popupRootRef.current = ReactDOM.createRoot(popupContainerRef.current);
        }
        return () => {
            if (popupRootRef.current) {
                setTimeout(() => {
                    popupRootRef.current?.unmount();
                    popupRootRef.current = null;
                    popupContainerRef.current = null;
                }, 0);
            }
        };
    }, []);

    // Initialize marker cluster group
    useEffect(() => {
        if (!map || !clustered) {
            return;
        }

        // Create marker cluster group with default Leaflet styling
        markerClusterRef.current = new L.MarkerClusterGroup({
            spiderfyOnMaxZoom: false,
            zoomToBoundsOnClick: true,
            showCoverageOnHover: false,
            maxClusterRadius: 30,
            disableClusteringAtZoom: map.getMaxZoom(),
            // Basic settings for smooth operation
            chunkedLoading: true,
            animate: false,
            iconCreateFunction: (cluster) => {
                // Calculate total number of actual markers in the cluster
                let totalCount = 0;
                cluster.getAllChildMarkers().forEach((marker: L.Marker) => {
                    const markerCount = (marker as any)._markerCount || 1;
                    totalCount += markerCount;
                });

                // Create default cluster icon with correct count
                const childCount = totalCount;
                let c = " marker-cluster-";
                if (childCount < 10) {
                    c += "small";
                } else if (childCount < 100) {
                    c += "medium";
                } else {
                    c += "large";
                }

                return new L.DivIcon({
                    html: "<div><span>" + childCount + "</span></div>",
                    className: "marker-cluster" + c,
                    iconSize: new L.Point(40, 40),
                });
            },
        });

        const onClusterClick = (e: LeafletEvent) => {
            if (!map) {
                return;
            }
            const cluster = e.layer;
            const markers = cluster.getAllChildMarkers() as L.Marker[];
            const markersAtSamePoint = verifyMarkersAreUnderSamePoint(markers);
            const isLeafCluster = cluster._childClusters.length === 0;

            if (markers && isLeafCluster && markersAtSamePoint) {
                // For markers at same point, show popup instead of zooming
                const firstMarker = markers[0];
                firstMarker.openPopup();
                L.DomEvent.stopPropagation(e);
                return;
            }

            // Otherwise zoom to bounds
            cluster.zoomToBounds({
                padding: [20, 20],
                maxZoom: map.getMaxZoom(),
            });
        };

        // Ensure cluster group is interactive
        markerClusterRef.current.on("clusterclick", onClusterClick);

        // Add cluster group to map first
        map.addLayer(markerClusterRef.current);

        // Now we can safely access the pane
        const markerPane = map.getPane("markerPane");
        if (markerPane) {
            markerPane.style.zIndex = "600";
        }

        return () => {
            if (markerClusterRef.current) {
                map.removeLayer(markerClusterRef.current);
                markerClusterRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, clustered]);

    // Cleanup markers when component unmounts
    useEffect(() => {
        return () => {
            markersRef.current.forEach((marker) => {
                marker.remove();
            });
            markersRef.current = [];
        };
    }, []);

    // Create and update markers
    useEffect(() => {
        if (!map || !popupRootRef.current || !popupContainerRef.current) return;

        // Remove old markers and clear cluster group
        markersRef.current.forEach((marker) => {
            if (clustered && markerClusterRef.current) {
                markerClusterRef.current.removeLayer(marker);
            }
            marker.remove();
        });
        markersRef.current = [];

        // Clear cluster group completely
        if (clustered && markerClusterRef.current) {
            markerClusterRef.current.clearLayers();
        }

        // Group markers by coordinates
        const markersByLocation = markers.reduce((acc, marker) => {
            const key = `${marker.coordinates[0]},${marker.coordinates[1]}`;
            if (!acc[key]) {
                acc[key] = [];
            }
            acc[key].push(marker);
            return acc;
        }, {} as Record<string, MapMarker[]>);

        // Create markers for each unique location
        Object.entries(markersByLocation).forEach(([coordKey, markersAtLocation]) => {
            const [lat, lng] = coordKey.split(",").map(Number);
            const latLng = new L.LatLng(lat, lng);

            // Create custom icon if there are multiple markers at this location
            let markerIcon;
            if (markersAtLocation.length > 1) {
                markerIcon = new L.DivIcon({
                    html: `
                        <img src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png" class="leaflet-marker-icon">
                        <img src="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png" class="leaflet-marker-shadow">
                        <div class="marker-badge">${markersAtLocation.length}</div>
                    `,
                    className: "marker-with-badge",
                    iconSize: [25, 41],
                    iconAnchor: [12, 41],
                    popupAnchor: [1, -34],
                });
            }

            // Create marker with appropriate icon
            const marker = L.marker(latLng, {
                interactive: true,
                bubblingMouseEvents: false,
                icon: markerIcon || new L.Icon.Default(),
            });

            // Store the actual count of markers at this location
            (marker as any)._markerCount = markersAtLocation.length;

            // Create popup content
            if (markersAtLocation.some((m) => m.popupContent)) {
                const contents = markersAtLocation.map((m) => m.popupContent).filter(Boolean);
                const popupContainer = document.createElement("div");
                ReactDOM.createRoot(popupContainer).render(<PopupContent contents={contents} />);

                marker.bindPopup(popupContainer, {
                    autoPan: true,
                    closeButton: true,
                });
            }

            // Add click handler with proper event handling
            marker.on("click", (e: L.LeafletMouseEvent) => {
                console.log("Marker clicked:", lat, lng);
                if (e.originalEvent) {
                    L.DomEvent.stopPropagation(e.originalEvent);
                    L.DomEvent.preventDefault(e.originalEvent);
                }
                marker.openPopup();
            });

            // Add marker to appropriate container (but not both)
            if (clustered && markerClusterRef.current) {
                markerClusterRef.current.addLayer(marker);
            } else {
                marker.addTo(map);
            }

            markersRef.current.push(marker);
        });
    }, [map, markers, clustered]);

    function verifyMarkersAreUnderSamePoint(markers: L.Marker[]): boolean {
        if (markers.length <= 1) {
            return true;
        }
        const firstPos = markers[0].getLatLng();
        return markers.every((marker) => {
            const pos = marker.getLatLng();
            return pos.lat === firstPos.lat && pos.lng === firstPos.lng;
        });
    }

    return null;
});

export default MapMarkersPane;
